import { useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import MESSAGE_TYPES from '../constants/MESSAGE_TYPES';
import Messager from '../lib/messager';
import type { ITicketsController } from '../lib/widget/tickets/tickets.interface';
import { useInjection } from 'inversify-react';
import { TICKETS_MODULE_IDS } from '../lib/widget/tickets/tickets.types';

const findSelectedTicket = (data, ticket, ticketsController) => {
  const {
    selectedEvent,
    meta: { isPeriodical },
  } = window.Widget.metaController;

  const selectedTicket = ticketsController.selectedTickets.find((currentTicket) => {
    if (ticket) {
      return currentTicket.announcement_id === ticket.announcement_id;
    }

    if (isPeriodical) {
      return currentTicket.id === data.id && currentTicket.selectedEvent === selectedEvent;
    }

    return currentTicket.id === data.id && `${selectedEvent}_${data.id}`;
  });

  return selectedTicket;
};

const useTicketsCountModal = () => {
  const ticketsController: ITicketsController = useInjection(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);
  const { Widget } = window;

  const [state, setState] = useState({
    count: 0,
    data: null,
    ticket: null,
  });

  useEffect(() => {
    ticketsController.addListener(ticketsController.events.SHOW_TICKETS_COUNT_MODAL, show);
    ticketsController.addListener(ticketsController.events.REMOVE, updateSelectedTickets);
    ticketsController.addListener(
      ticketsController.events.SET_NEW_SELECTED_TICKETS,
      getSelectedTickets,
    );

    return () => {
      ticketsController.removeListener(ticketsController.events.SHOW_TICKETS_COUNT_MODAL, show);
      ticketsController.removeListener(ticketsController.events.REMOVE, updateSelectedTickets);
      ticketsController.removeListener(
        ticketsController.events.SET_NEW_SELECTED_TICKETS,
        getSelectedTickets,
      );
    };
  }, []);

  const stateRef = useRef<{
    count: number;
    data: any;
    ticket: any;
  }>();

  stateRef.current = state;

  const getSelectedTickets = () => {
    const { data, ticket } = stateRef.current;

    if (!data) return;

    const selectedTicket = findSelectedTicket(data, ticket, ticketsController);

    setState((prevState) => ({
      ...prevState,
      count: selectedTicket ? selectedTicket.count : 0,
    }));
  };

  const show = (data, ticket) => {
    if (window.Widget.isPool) {
      const count = ticket
        ? get(
            Widget.ticketsController.selectedTicketsCounts,
            `${data.id}.tickets.${ticket.announcement_id}.count`,
            0,
          )
        : get(
            Widget.ticketsController.selectedTicketsCounts,
            `${data.id}.tickets.${data.announcement_id}.count`,
            0,
          );

      return setState({
        data,
        count,
        ticket: ticket || data,
      });
    }

    const selectedTicket = findSelectedTicket(data, ticket, ticketsController);
    let count = 0;

    if (selectedTicket) {
      ({ count } = selectedTicket);
    }

    Messager.send({
      type: MESSAGE_TYPES.CHANGE_HELP_STATE,
      isOpen: true,
    });

    setState({
      data,
      count,
      ticket,
    });
  };

  const hide = () => {
    Messager.send({
      type: MESSAGE_TYPES.CHANGE_HELP_STATE,
      isOpen: false,
    });

    setState((prevState) => ({
      ...prevState,
      data: null,
      count: 0,
    }));
  };

  const updateCount = (_, count) => {
    const { data, ticket } = state;
    const isRemove = count < state.count;
    const crypto = get(ticket, 'crypto', data.crypto);
    const sellerId = get(ticket, 'seller_id', data.seller_id);
    const announcementId = get(ticket, 'announcement_id', data.announcement_id);
    const { selectedEvent } = window.Widget.metaController;

    setState((prevState) => ({
      ...prevState,
      count,
    }));

    const ticketData = {
      ...data,
      ...(sellerId && { seller_id: sellerId }),
      ...(crypto && { crypto }),
      ...(announcementId && { announcement_id: announcementId }),
      ...(selectedEvent && { selectedEvent }),
      count: count || 0,
    };

    if (isRemove) {
      return ticketsController.removeCounterTicket(ticketData);
    }

    return ticketsController.addCounterTicket(ticketData);
  };

  const updatePoolCount = (_, count) => {
    const { data, ticket } = state;

    setState((prevState) => ({
      ...prevState,
      count,
    }));

    ticketsController.updatePoolCounterTicket(ticket, count, data);
  };

  const updateSelectedTickets = (ticket) => {
    if (!ticket || ticket.category) return;

    setState((prevState) => ({
      ...prevState,
      count: 0,
    }));
  };

  return {
    state,
    hide,
    updateCount,
    updatePoolCount,
  };
};

export default useTicketsCountModal;
